import React, { memo, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { TicketIcon, User } from 'lucide-react';
import { toast } from 'sonner';
import { ticketsAPI } from '../../api';

const InlineTicketDialog = memo(({ 
  open, 
  onOpenChange, 
  contactId,
  contactName,
  teamMembers = [],
  onSuccess 
}) => {
  const [form, setForm] = useState({
    subject: '',
    description: '',
    type: 'support',
    priority: 'medium',
    assigned_to: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }

    try {
      setLoading(true);
      
      await ticketsAPI.create({
        subject: form.subject,
        description: form.description,
        type: form.type,
        priority: form.priority,
        status: 'open',
        contact_id: contactId,
        assigned_to: form.assigned_to && form.assigned_to !== '__none__' ? form.assigned_to : null
      });
      
      toast.success('Ticket created successfully');
      setForm({
        subject: '',
        description: '',
        type: 'support',
        priority: 'medium',
        assigned_to: ''
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setLoading(false);
    }
  }, [form, contactId, onOpenChange, onSuccess]);

  const handleClose = useCallback(() => {
    setForm({
      subject: '',
      description: '',
      type: 'support',
      priority: 'medium',
      assigned_to: ''
    });
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TicketIcon className="h-5 w-5 text-red-600" />
            Create Support Ticket
          </DialogTitle>
          <DialogDescription>
            Create a ticket for {contactName}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ticket-subject">Subject *</Label>
            <Input
              id="ticket-subject"
              value={form.subject}
              onChange={(e) => setForm(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Brief description of the issue"
              data-testid="inline-ticket-subject"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm(prev => ({ ...prev, type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="support">Support</SelectItem>
                  <SelectItem value="bug">Bug Report</SelectItem>
                  <SelectItem value="feature">Feature Request</SelectItem>
                  <SelectItem value="billing">Billing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm(prev => ({ ...prev, priority: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Assign To</Label>
            <Select 
              value={form.assigned_to || '__none__'} 
              onValueChange={(v) => setForm(prev => ({ ...prev, assigned_to: v }))}
            >
              <SelectTrigger>
                <User className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Select team member..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {teamMembers.map(user => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.first_name} {user.last_name || user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="ticket-description">Description *</Label>
            <Textarea
              id="ticket-description"
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Detailed description of the issue..."
              rows={4}
              data-testid="inline-ticket-description"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !form.subject.trim()}>
              {loading ? 'Creating...' : 'Create Ticket'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});

InlineTicketDialog.displayName = 'InlineTicketDialog';

export default InlineTicketDialog;
